import { useEffect, useState } from "react";
import TopNav from "../components/TopNav.jsx";
import ProductMark from "../components/ProductMark.jsx";
import { getToken, isMaster } from "../lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * VidSmithPage — Sage's motion-video studio.
 * Backend: /api/vidsmith · buyer picks Veo 3 / Sora 2 / Higgsfield / fal.ai.
 * fal.ai rail is wired today; the other three light when the Founder signs.
 */
export default function VidSmithPage() {
  const [shelf, setShelf] = useState(null);
  const [health, setHealth] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [seconds, setSeconds] = useState(5);
  const [provider, setProvider] = useState("fal.ai");
  const [aspect, setAspect] = useState("16:9");
  const [rendering, setRendering] = useState(false);
  const [lastRender, setLastRender] = useState(null);

  const reload = async () => {
    try {
      const [p, h, l] = await Promise.all([
        fetch(`${BACKEND_URL}/api/vidsmith/providers`).then((r) => (r.ok ? r.json() : null)),
        fetch(`${BACKEND_URL}/api/vidsmith/health`).then((r) => (r.ok ? r.json() : null)),
        fetch(`${BACKEND_URL}/api/vidsmith/ledger`).then((r) => (r.ok ? r.json() : null)),
      ]);
      setShelf(p);
      setHealth(h);
      setLedger(l);
    } catch {}
  };

  useEffect(() => { reload(); }, []);

  const priceEstimate = () => {
    if (!shelf) return 0;
    const row = shelf.shelf?.find((r) => r.provider === provider);
    if (!row) return 0;
    return row.price_per_second_usd * Number(seconds || 0);
  };

  const doRender = async () => {
    if (!prompt.trim() || prompt.trim().length < 3) {
      toast.error("Prompt too short — at least 3 characters.");
      return;
    }
    setRendering(true);
    setLastRender(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/vidsmith/render`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          prompt,
          seconds: Number(seconds),
          provider,
          aspect_ratio: aspect,
        }),
      });
      const j = await r.json();
      if (!r.ok) {
        setLastRender({ ok: false, ...j });
        toast.error(j?.detail?.message || j?.detail || "Render failed.");
      } else {
        setLastRender({ ok: true, ...j });
        toast.success(`Queued · ${j.render_id}`);
        setPrompt("");
      }
      reload();
    } catch (e) {
      toast.error(String(e));
    } finally {
      setRendering(false);
    }
  };

  const master = isMaster();

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text">
      <TopNav />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-8 flex items-start gap-4">
          <div className="text-sage-accent">
            <ProductMark slug="vidsmith" size={44} />
          </div>
          <div>
            <h1 className="text-4xl font-bold mb-2" data-testid="vidsmith-title">VidSmith</h1>
            <div className="text-sm text-sage-muted leading-relaxed max-w-2xl">
              Motion video studio. Text-to-video, image-to-video, character-consistent shots. Four rails on the shelf — you pick, you pay only for what you render.
            </div>
          </div>
        </div>

        {/* Health strip */}
        {health && (
          <div className="border border-sage-border p-4 mb-6 text-[11px] uppercase tracking-[0.15em] text-sage-faint flex flex-wrap gap-4" data-testid="vidsmith-health">
            <span>Wired now: <span className="text-sage-accent">{(health.providers_wired || []).join(" · ") || "none"}</span></span>
            <span>Planned: <span className="text-sage-muted">{(health.providers_planned || []).join(" · ") || "none"}</span></span>
            <span>Family default: <span className="text-sage-text">{health.family_default_provider}</span></span>
          </div>
        )}

        {/* Provider shelf */}
        <div className="border border-sage-border p-6 mb-8" data-testid="vidsmith-shelf">
          <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-4">
            The shelf · pick your rail
          </div>
          {!shelf && <div className="text-xs text-sage-faint">Loading…</div>}
          {shelf && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {shelf.shelf.map((row) => {
                const wired = row.status === "wired";
                const isPicked = provider === row.provider;
                return (
                  <button
                    key={row.provider}
                    type="button"
                    onClick={() => wired && setProvider(row.provider)}
                    disabled={!wired}
                    data-testid={`vidsmith-shelf-${row.provider}`}
                    className={
                      "text-left border p-4 transition " +
                      (isPicked
                        ? "border-sage-accent bg-sage-surface"
                        : wired
                        ? "border-sage-border hover:border-sage-accent cursor-pointer"
                        : "border-sage-border opacity-50 cursor-not-allowed")
                    }
                  >
                    <div className="flex items-baseline justify-between mb-1">
                      <div className="text-lg font-bold text-sage-text uppercase tracking-tight">{row.provider}</div>
                      <div className="text-sm text-sage-accent font-bold tabular-nums">
                        ${row.price_per_second_usd.toFixed(2)}/sec
                      </div>
                    </div>
                    <div className="text-xs text-sage-muted mb-2">{row.best_for}</div>
                    <div className={"text-[10px] uppercase tracking-[0.15em] " + (wired ? "text-sage-accent" : "text-sage-faint")}>
                      {wired ? "wired · ready to render" : "on the shelf · waits on Founder-signed access"}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Render form */}
        {master ? (
          <div className="border border-sage-border p-6 mb-8" data-testid="vidsmith-render-form">
            <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-3">
              Render a clip
            </div>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe the shot — subject, motion, mood, camera, lens. Be specific. VidSmith prefers detail."
              className="w-full h-32 bg-sage-input border border-sage-border p-3 text-sm font-mono resize-y focus:outline-none focus:border-sage-accent"
              disabled={rendering}
              data-testid="vidsmith-prompt-input"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-sage-faint block mb-1">Seconds</label>
                <input
                  type="number"
                  min={1}
                  max={60}
                  step={1}
                  value={seconds}
                  onChange={(e) => setSeconds(e.target.value)}
                  className="w-full bg-sage-input border border-sage-border px-3 py-2 text-sm text-sage-text focus:outline-none focus:border-sage-accent tabular-nums"
                  data-testid="vidsmith-seconds-input"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-sage-faint block mb-1">Aspect ratio</label>
                <select
                  value={aspect}
                  onChange={(e) => setAspect(e.target.value)}
                  className="w-full bg-sage-input border border-sage-border px-3 py-2 text-sm text-sage-text focus:outline-none focus:border-sage-accent"
                  data-testid="vidsmith-aspect-input"
                >
                  <option value="16:9">16:9 · widescreen</option>
                  <option value="9:16">9:16 · vertical / shorts</option>
                  <option value="1:1">1:1 · square</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-sage-faint block mb-1">Estimated cost</label>
                <div className="w-full bg-sage-input border border-sage-border px-3 py-2 text-sm text-sage-accent font-bold tabular-nums" data-testid="vidsmith-cost-estimate">
                  ${priceEstimate().toFixed(2)}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between mt-4">
              <div className="text-[10px] text-sage-faint uppercase tracking-[0.15em]">
                {prompt.length} chars · rail: {provider}
              </div>
              <button
                onClick={doRender}
                disabled={rendering || prompt.trim().length < 3}
                className="px-6 py-2 bg-sage-accent text-sage-bg text-xs uppercase tracking-[0.15em] disabled:opacity-40 hover:bg-sage-accent-hover"
                data-testid="vidsmith-render-btn"
              >
                {rendering ? "Rendering…" : "Render"}
              </button>
            </div>
            {lastRender && (
              <div
                className={"mt-4 border p-3 text-xs " + (lastRender.ok ? "border-sage-accent" : "border-sage-warn")}
                data-testid="vidsmith-last-render"
              >
                <div className="font-bold mb-1">{lastRender.ok ? "Queued ↓" : "Rejected ↓"}</div>
                <pre className="text-sage-muted whitespace-pre-wrap text-[11px]">
                  {JSON.stringify(lastRender, null, 2)}
                </pre>
              </div>
            )}
          </div>
        ) : (
          <div className="border border-sage-border p-6 mb-8 text-sm text-sage-muted" data-testid="vidsmith-not-master">
            The render bench is master-only until public rails open. The shelf above and the ledger below are public — every render lands there with its prompt, seconds, and dollar figure.
          </div>
        )}

        {/* Ledger */}
        <div className="border border-sage-border p-6" data-testid="vidsmith-ledger">
          <div className="text-xs uppercase tracking-[0.15em] text-sage-muted mb-4">
            The ledger · every render, every rail
          </div>
          {!ledger && <div className="text-xs text-sage-faint">Loading…</div>}
          {ledger && (
            <>
              <div className="text-2xl font-bold text-sage-accent tabular-nums mb-4" data-testid="vidsmith-total-renders">
                {ledger.totals.renders} total renders
              </div>
              {Object.keys(ledger.by_provider || {}).length === 0 ? (
                <div className="text-xs text-sage-faint italic">
                  No renders yet. When VidSmith fires, every one will land here.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(ledger.by_provider).map(([p, v]) => (
                    <div key={p} className="border border-sage-border p-3" data-testid={`vidsmith-ledger-${p}`}>
                      <div className="text-sm font-bold text-sage-text uppercase tracking-tight">{p}</div>
                      <div className="text-xs text-sage-muted tabular-nums mt-1">
                        {v.count} renders · ${v.revenue_usd?.toFixed(2)} revenue
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
